import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ShieldCheck, AlertCircle } from "lucide-react";
import snapshotLogo from "@/assets/snapshot-logo.png";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

/**
 * Two-Step Password Reset Verification Page
 * 
 * This page acts as an intermediate landing page for password reset emails.
 * Email security scanners often pre-fetch links to check for malware, which
 * can invalidate one-time tokens. By requiring the user to click a button,
 * we ensure only real users consume the token.
 * 
 * Redirects to Supabase's /auth/v1/verify endpoint for token verification.
 */

const SUPABASE_URL = "https://ndqudoynysmynztrepwj.supabase.co";

const VerifyPasswordReset = () => {
  const [searchParams] = useSearchParams();
  const [isVerifying, setIsVerifying] = useState(false);
  const [hasValidParams, setHasValidParams] = useState(false);

  // Extract URL parameters from the email link
  const token = searchParams.get("token");
  const type = searchParams.get("type");
  const email = searchParams.get("email");

  useEffect(() => {
    // Validate that we have the minimum required params (token, type, and email for verifyOtp)
    setHasValidParams(!!token && !!type && !!email);
  }, [token, type, email]);

  const handleResetClick = () => {
    if (!token || !type || !email) return;

    setIsVerifying(true);

    // Store verification data in sessionStorage (secure, same-tab only)
    // This prevents the token from being consumed by server-side redirects
    sessionStorage.setItem('passwordResetData', JSON.stringify({
      token,
      type,
      email
    }));
    
    // Navigate to reset page with clean URL (no token exposure)
    window.location.href = "/reset-password";
  };

  // Show invalid params state
  if (!hasValidParams) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
              <BrandLogo fallback={snapshotLogo} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-center">
                <AlertCircle className="h-12 w-12 text-destructive" />
              </div>
              <CardTitle className="text-xl">Invalid Reset Link</CardTitle>
              <CardDescription>
                This password reset link is invalid or has expired. Please request a new password reset.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => window.location.href = "/login"}
            >
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            <BrandLogo fallback={snapshotLogo} />
          </div>
          <div className="space-y-2">
            <div className="flex justify-center">
              <ShieldCheck className="h-12 w-12 text-primary" />
            </div>
            <CardTitle className="text-xl">Reset Your Password</CardTitle>
            <CardDescription>
              Click the button below to securely reset your password.
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button
            onClick={handleResetClick}
            disabled={isVerifying}
            className="w-full"
            size="lg"
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Reset Password"
            )}
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            If you didn't request a password reset, you can safely ignore this page.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifyPasswordReset;

function BrandLogo({ fallback }: { fallback: string }) {
  const { brand, isWhiteLabel } = useBrand();
  const logo = isWhiteLabel ? brand?.config.logo_url : null;
  const name = isWhiteLabel ? brand?.config.company_name || "Logo" : "Snapshot Live";
  return <img src={logo || fallback} alt={name} className={isWhiteLabel ? "brand-reset-logo object-contain" : "h-16 w-auto object-contain"} style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} />;
}
